import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listItemPlanning, type ItemPlanningDto } from './api';
import { PlanningFormModal } from './PlanningFormModal';
import { AbcSummaryCard } from './AbcSummaryCard';

const PLANNING_KEY = 'item-planning';

/**
 * Inventory Planning — per-warehouse item settings.
 *
 * Warehouse-scoped by nature: the same item has different planning in each warehouse, so
 * there is no useful warehouse-agnostic view. That is also why these settings live here and
 * not on the item master (gap-analysis D3).
 */
export function PlanningRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canWrite = permissions.has('items:write');

  // Min/max are per item PER WAREHOUSE, so this screen has always needed one. It used to pick
  // the first and offer a dropdown; the header answers it now, for every screen at once.
  const warehouseId = useCurrentWarehouseId();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ItemPlanningDto | null>(null);
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const planning = useQuery({
    queryKey: [PLANNING_KEY, warehouseId || 'none'] as const,
    queryFn: ({ signal }) => listItemPlanning(warehouseId, false, signal),
    enabled: Boolean(warehouseId),
  });

  const rows = useMemo(() => {
    const all = planning.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (r) =>
        r.itemNumber.toLowerCase().includes(needle) ||
        r.description.toLowerCase().includes(needle),
    );
  }, [planning.data, search]);

  function edit(row: ItemPlanningDto) {
    setEditing(row);
    openEdit();
  }

  return (
    <Stack gap="lg">
      <PageHeader title={t('planning.title')} subtitle={t('planning.description')} />

      <Card withBorder padding="md">
        <Group align="flex-end" wrap="wrap" gap="md">
          <TextInput
            label={t('planning.search')}
            placeholder={t('planning.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ minWidth: 240, flex: 1 }}
          />
        </Group>
      </Card>

      {/* Above the grid, because the class in the ABC column is meaningless without knowing
          when it was last calculated — or that it never has been. */}
      {warehouseId && (
        <AbcSummaryCard
          warehouseId={warehouseId}
          canWrite={canWrite}
          onClassified={() => {
            void queryClient.invalidateQueries({ queryKey: [PLANNING_KEY, warehouseId] });
          }}
        />
      )}

      {planning.isError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('planning.errors.loadFailed')}
        </Alert>
      )}

      {warehouseId && planning.isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {/* An empty grid here does not mean "nothing to plan" — it means no item is assigned
          to this warehouse yet, which is a different problem with a different fix. */}
      {planning.isSuccess && rows.length === 0 && (
        <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {search.trim() ? t('planning.noMatches') : t('planning.noAssignments')}
        </Alert>
      )}

      {planning.isSuccess && rows.length > 0 && (
        <Card withBorder padding={0}>
          <Table.ScrollContainer minWidth={1100}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('planning.item')}</Table.Th>
                  <Table.Th>{t('planning.uomShort')}</Table.Th>
                  <Table.Th ta="right">{t('planning.minShort')}</Table.Th>
                  <Table.Th ta="right">{t('planning.maxShort')}</Table.Th>
                  <Table.Th ta="right">{t('planning.leadShort')}</Table.Th>
                  <Table.Th>{t('planning.abcShort')}</Table.Th>
                  <Table.Th>{t('planning.nextCountShort')}</Table.Th>
                  <Table.Th>{t('planning.flags')}</Table.Th>
                  <Table.Th w={60} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.itemId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {r.itemNumber}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {r.description}
                      </Text>
                    </Table.Td>
                    <Table.Td>{r.stockingUomCode}</Table.Td>
                    <Table.Td ta="right">{fmt(r.minQuantity)}</Table.Td>
                    <Table.Td ta="right">{fmt(r.maxQuantity)}</Table.Td>
                    <Table.Td ta="right">{r.leadTimeDays ?? '—'}</Table.Td>
                    <Table.Td>
                      {/* No badge when unclassified: an empty cell reads as "not calculated
                          yet", whereas a badge would imply a real classification. */}
                      {r.currentAbcClass ? <Badge size="sm">{r.currentAbcClass}</Badge> : '—'}
                    </Table.Td>
                    <Table.Td>
                      {r.nextCountDueAtUtc
                        ? new Date(r.nextCountDueAtUtc).toLocaleDateString()
                        : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {!r.isStocked && (
                          <Badge size="xs" color="gray" variant="light">
                            {t('planning.notStocked')}
                          </Badge>
                        )}
                        {r.isCountControlGroup && (
                          <Badge size="xs" color="blue" variant="light">
                            {t('planning.controlGroupShort')}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={canWrite ? t('planning.edit') : t('planning.readOnly')}>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={() => edit(r)}
                          disabled={!canWrite}
                          aria-label={t('planning.editAria', { item: r.itemNumber })}
                        >
                          <IconPencil size={16} />
                        </Button>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {editing && (
        <PlanningFormModal
          opened={editOpen}
          onClose={closeEdit}
          warehouseId={warehouseId}
          row={editing}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: [PLANNING_KEY, warehouseId] });
          }}
        />
      )}
    </Stack>
  );
}

function fmt(v: number | null): string {
  if (v === null) return '—';
  // Trailing zeros from decimal(19,5) are noise in a grid; keep them only when meaningful.
  return v.toLocaleString(undefined, { maximumFractionDigits: 5 });
}
